import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import Chip from "@mui/material/Chip";
import { ReactComponent as SearchIcon } from "../../assets/Icons/search.svg";
import { ReactComponent as CloseIcon } from "../../assets/Icons/close.svg";
import { useNetwork } from "../../contexts/NetWork";
import {
  LOADING,
  connections,
  treatment_program,
} from "../../constants/common";
import CardLoader from "../../components/CardLoader";
import UserCard from "../../components/UserCard";
import ReferalModal from "../../components/ReferalModal";
import SendToFriendModal from "../../components/SendToFriendModal";
import ConnectionsModal from "../../components/ConnectionsModal";
import { useUserProfile } from "../../contexts/UserProfile";
import { useSearchParams } from "react-router-dom";

let timer;
let tabTimer;
const UserConnections = ({ other }) => {
  const [searchParams] = useSearchParams();
  const account_type = searchParams.get("account_type");
  const { fetchNetwork, fetchOthersNetwork, fetchStatus, network } =
    useNetwork();
  const { data: userData } = useUserProfile();

  const [params, setParams] = useState({
    page: 1,
    search: "",
    account_type: account_type,
    provider: "therapist",
  });

  const [activeTab, setActiveTab] = useState(0);

  const chipOptions = [
    { label: "Therapists", value: "therapist" },
    { label: "Tx Programs & Group Practices", value: "treatment_program" },
  ];

  const getActiveChipValue = () => {
    return chipOptions[activeTab]?.value || "therapist";
  };

  const [activeUser, setActiveUser] = useState({});
  const [openReferral, setOpenReferral] = useState(false);

  const handleOpenReferralModal = (data) => {
    setActiveUser(data);
    setOpenReferral(true);
  };

  const handleCloseReferralModal = () => {
    setOpenReferral(false);
  };

  // "Share Info" and "Who they trust" render unconditionally in UserCard
  // (enableShare / enableConnections both default to true), but their handlers
  // default to null -- so without these the share icon threw and the link did
  // nothing. Mirrors the wiring in pages/Network/index.jsx.
  const [openShare, setOpenShare] = useState(false);
  const [openOtherNetwork, setOpenOtherNetwork] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleOpenShareModal = (data) => {
    setActiveUser(data);
    setOpenShare(true);
  };

  const handleCloseShareModal = () => setOpenShare(false);

  const handleOpenOtherConnection = (data) => {
    setSelectedUser(data);
    setOpenOtherNetwork(true);
  };

  const handleCloseConnections = () => {
    const payload = {
      ...params,
      user_id:
        other && account_type === treatment_program
          ? userData.info?.id
          : userData.id,
    };
    if (other) {
      fetchOthersNetwork(payload, connections, undefined, true);
    } else {
      fetchNetwork(payload, connections, undefined, true);
    }
    setOpenOtherNetwork(false);
  };

  const handleSearch = (ev) => {
    setParams((prevState) => ({
      ...prevState,
      search: ev.target.value,
      page: 1,
    }));
  };

  const clearSearch = () => {
    setParams((prevState) => ({
      ...prevState,
      search: "",
      page: 1,
    }));
    if (other) {
      fetchOthersNetwork(
        {
          ...params,
          user_id:
            account_type === treatment_program
              ? userData.info?.id
              : userData.id,
        },
        connections,
        undefined,
        true
      );
    } else {
      fetchNetwork(
        {
          ...params,
          user_id: userData.id,
        },
        connections,
        undefined,
        true
      );
    }
  };

  const handlePagination = (_, page) => {
    setParams((prevState) => ({
      ...prevState,
      page,
    }));
  };

  const handleChipChange = (chipIndex) => {
    setActiveTab(chipIndex);
    setParams((prevState) => ({
      ...prevState,
      page: 1,
    }));
    
    setParams((prevState) => ({
      ...prevState,
      provider: chipOptions[chipIndex].value,
    }));

    clearTimeout(tabTimer);
    tabTimer = setTimeout(() => {
      const payload = {
        ...params,
        page: 1,
        provider: chipOptions[chipIndex].value,
        user_id:
          account_type === treatment_program ? userData?.info?.id : userData.id,
      };

      if (other) {
        fetchOthersNetwork(payload, connections, undefined, true);
      } else {
        fetchNetwork(payload, connections, undefined, true);
      }
    }, 100);
  };

  useEffect(() => {
    if (params.search?.length > 2) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (other) {
          fetchOthersNetwork(
            {
              ...params,
              user_id:
                account_type === treatment_program
                  ? userData?.info?.id
                  : userData.id,
            },
            connections,
            undefined,
            true
          );
        } else {
          fetchNetwork(
            {
              ...params,
              user_id: userData.id,
            },
            connections,
            undefined,
            true
          );
        }
      }, 1000);
    }
  }, [params.search]);

  useEffect(() => {
    if (other) {
      fetchOthersNetwork(
        {
          ...params,
          user_id:
            account_type === treatment_program
              ? userData?.info?.id
              : userData.id,
        },
        connections,
        undefined,
        true
      );
    } else {
      fetchNetwork(
        {
          ...params,
          user_id: userData.id,
        },
        connections,
        undefined,
        true
      );
    }
  }, [params.page]);

  return (
    <Paper
      sx={{
        boxShadow: "none",
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        {chipOptions.map((chip, index) => (
          <Chip
            key={chip.value}
            label={chip.label}
            onClick={() => handleChipChange(index)}
            variant={activeTab === index ? "filled" : "outlined"}
            color={activeTab === index ? "primary" : "default"}
          />
        ))}
      </Box>

      <TextField
        fullWidth
        placeholder="Search by Therapist Name, Email or Address"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: params.search && (
            <InputAdornment position="end">
              <IconButton
                onClick={clearSearch}
                sx={{
                  p: 0,
                  color: "border.dark",
                }}
              >
                <CloseIcon width={20} height={20} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "input:not(textarea)": {
            pt: { xs: 1.75, sm: 1.5 },
            pb: 1.5,
            fontSize: { xs: 12, sm: 14 },
            pl: 0,
          },
        }}
        onChange={handleSearch}
        value={params.search || ""}
        name="search"
      />

      {fetchStatus === LOADING ? (
        <>
          <CardLoader />
          <CardLoader />
          <CardLoader />
          <CardLoader />
        </>
      ) : (
        network.data.map((item) => (
          <UserCard
            key={item.id}
            data={{ ...item, is_treatment: activeTab === 1 }}
            onReferralClick={handleOpenReferralModal}
            onShareClick={handleOpenShareModal}
            handleOpenOtherConnection={handleOpenOtherConnection}
            clearSearch={clearSearch}
            enableRemove={other ? false : true}
            enableReferral={(item.user_referral && item.send_ref) ? true : false}
            params={{ ...params, user_id: userData.id }}
          />
        ))
      )}

      {network.last_page > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 3,
          }}
        >
          <Pagination
            count={network.last_page}
            shape="rounded"
            color="primary"
            onChange={handlePagination}
            page={params.page}
          />
        </Box>
      )}

      <ReferalModal
        open={openReferral}
        onClose={handleCloseReferralModal}
        data={activeUser}
        isProfile={"user"}
      />

      <SendToFriendModal
        open={openShare}
        onClose={handleCloseShareModal}
        data={activeUser}
      />

      {openOtherNetwork && (
        <ConnectionsModal
          other={openOtherNetwork}
          selectedUser={selectedUser}
          provider={
            getActiveChipValue() === "treatment_program"
              ? treatment_program
              : "user"
          }
          handleClose={handleCloseConnections}
        />
      )}
    </Paper>
  );
};

export default UserConnections;
